import {
  useEffect,
  useId,
  useRef,
  useState,
  type PointerEvent,
  type KeyboardEvent,
} from "react";
import { AssetImage } from "./AssetImage";
import type { AssetLoader } from "./images";
import {
  drawCrop,
  moveCrop,
  resizeCrop,
  type Crop,
  type CropPoint,
  type CropHandle,
} from "./crop-selection";

const handles = ["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const;
const names = {
  nw: "top left",
  n: "top",
  ne: "top right",
  e: "right",
  se: "bottom right",
  s: "bottom",
  sw: "bottom left",
  w: "left",
};
export function ImageCropSelection({
  assetId,
  assetUrl,
  loadAsset,
  crop,
  onChange,
  disabled,
  aspect,
  size,
  onDimensions,
}: {
  assetId: string;
  assetUrl: (id: string) => string;
  loadAsset?: AssetLoader;
  crop: Crop;
  onChange(crop: Crop): void;
  disabled: boolean;
  aspect?: number;
  size?: { width: number; height: number };
  onDimensions(size: { width: number; height: number }): void;
}) {
  const description = useId();
  const [selection, setSelection] = useState(crop);
  const [dimensions, setDimensions] = useState(size);
  const [loaded, setLoaded] = useState(false);
  const [drawing, setDrawing] = useState(crop.width === 1 && crop.height === 1);
  const gesture = useRef<
    | {
        id: number;
        start: CropPoint;
        before: Crop;
        mode: "draw" | "move" | CropHandle;
        next: Crop;
        dirty: boolean;
      }
    | undefined
  >(undefined);
  useEffect(() => {
    if (!gesture.current) {
      setSelection(crop);
      if (crop.width === 1 && crop.height === 1) setDrawing(true);
    }
  }, [crop]);
  const ratio =
    aspect && dimensions
      ? (aspect * dimensions.height) / dimensions.width
      : undefined;
  const blocked = disabled || !loaded || (!!aspect && !ratio);
  useEffect(() => {
    if (blocked && gesture.current) {
      setSelection(gesture.current.before);
      gesture.current = undefined;
    }
  }, [blocked]);
  const point = (event: PointerEvent<HTMLDivElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(1, (event.clientX - box.left) / box.width)),
      y: Math.max(0, Math.min(1, (event.clientY - box.top) / box.height)),
    };
  };
  const key = (event: KeyboardEvent, handle?: CropHandle) => {
    if (
      blocked ||
      !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)
    )
      return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 0.05 : 0.005,
      dx =
        event.key === "ArrowLeft"
          ? -step
          : event.key === "ArrowRight"
            ? step
            : 0,
      dy =
        event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
    const next = handle
      ? resizeCrop(
          selection,
          handle,
          {
            x:
              (handle.includes("w")
                ? selection.x
                : selection.x + selection.width) + dx,
            y:
              (handle.includes("n")
                ? selection.y
                : selection.y + selection.height) + dy,
          },
          ratio,
        )
      : moveCrop(selection, dx, dy);
    setSelection(next);
    onChange(next);
  };
  return (
    <section className="proof-cropper" aria-label="Image crop selection">
      <h3>Crop original image</h3>
      <p id={description}>
        Drag outside the selection to draw a crop. Drag inside to move it, or
        drag an edge or corner to resize. Arrow keys move the focused selection
        or handle; Shift moves farther.
      </p>
      {aspect && <p>Fixed crop aspect: {aspect}:1</p>}

      <div
        className="proof-cropper-surface"
        style={{
          width: dimensions
            ? `min(100%, ${(360 * dimensions.width) / dimensions.height}px)`
            : "100%",
        }}
        aria-label="Crop image area"
        aria-disabled={blocked}
        onDragStart={(event) => event.preventDefault()}
        onLoadCapture={(event) => {
          const image = event.target;
          if (
            image instanceof HTMLImageElement &&
            image.naturalWidth &&
            image.naturalHeight
          ) {
            const next = {
              width: image.naturalWidth,
              height: image.naturalHeight,
            };
            setDimensions(next);
            onDimensions(next);
            setLoaded(true);
          }
        }}
        onPointerDown={(event) => {
          if (blocked || event.button !== 0 || gesture.current) return;
          event.preventDefault();
          event.stopPropagation();
          const target = event.target as HTMLElement,
            handle = target.closest<HTMLElement>("[data-crop-handle]")?.dataset
              .cropHandle as CropHandle | undefined;
          const mode =
            handle ||
            (drawing
              ? "draw"
              : target.closest("[data-crop-selection]")
                ? "move"
                : "draw");
          gesture.current = {
            id: event.pointerId,
            start: point(event),
            before: selection,
            mode,
            next: selection,
            dirty: false,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const drag = gesture.current;
          if (!drag || drag.id !== event.pointerId || blocked) return;
          const now = point(event);
          if (
            !drag.dirty &&
            Math.abs(now.x - drag.start.x) + Math.abs(now.y - drag.start.y) <
              0.002
          )
            return;
          drag.dirty = true;
          drag.next =
            drag.mode === "draw"
              ? drawCrop(drag.start, now, ratio)
              : drag.mode === "move"
                ? moveCrop(
                    drag.before,
                    now.x - drag.start.x,
                    now.y - drag.start.y,
                  )
                : resizeCrop(drag.before, drag.mode, now, ratio);
          setSelection(drag.next);
        }}
        onPointerUp={(event) => {
          const drag = gesture.current;
          if (!drag || drag.id !== event.pointerId) return;
          gesture.current = undefined;
          if (event.currentTarget.hasPointerCapture(event.pointerId))
            event.currentTarget.releasePointerCapture(event.pointerId);
          if (drag.dirty && !blocked) {
            onChange(drag.next);
            setDrawing(false);
          }
        }}
        onPointerCancel={() => {
          if (gesture.current) setSelection(gesture.current.before);
          gesture.current = undefined;
        }}
        onLostPointerCapture={() => {
          if (gesture.current) setSelection(gesture.current.before);
          gesture.current = undefined;
        }}
      >
        <AssetImage
          assetId={assetId}
          assetUrl={assetUrl}
          loadAsset={loadAsset}
          alt="Original image for crop selection"
        />
        {loaded && (
          <div
            data-crop-selection
            className="proof-cropper-selection"
            role="group"
            aria-label="Move crop selection"
            aria-describedby={description}
            aria-disabled={blocked}
            tabIndex={blocked ? -1 : 0}
            onKeyDown={(event) => key(event)}
            style={{
              left: `${selection.x * 100}%`,
              top: `${selection.y * 100}%`,
              width: `${selection.width * 100}%`,
              height: `${selection.height * 100}%`,
            }}
          >
            <span className="proof-cropper-grid" aria-hidden="true" />
            {handles.map((handle) => (
              <button
                key={handle}
                type="button"
                className={`proof-cropper-handle handle-${handle}`}
                data-crop-handle={handle}
                aria-label={`Resize crop ${names[handle]}`}
                aria-describedby={description}
                disabled={blocked}
                onKeyDown={(event) => key(event, handle)}
              />
            ))}
          </div>
        )}
      </div>
      <output aria-live="polite" aria-label="Selected crop">
        {Math.round(selection.width * 100)}% width ×{" "}
        {Math.round(selection.height * 100)}% height ·{" "}
        {Math.round(selection.x * 100)}% left, {Math.round(selection.y * 100)}%
        top
      </output>
    </section>
  );
}
